/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import type {
  BackgroundOpacity,
  CaptionTrack,
  FontFamily,
  FontSize,
  Level,
  SubtitlePosition,
  TextColor,
  TextEdgeStyle,
} from "@lumi/player-core";
import { Button } from "@lumi/ui-headless";
import { Check, Gauge, MonitorPlay, MoveVertical, Palette, Scan, Subtitles, Type, Wand2, X } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";

export type SettingsPanelProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  // Filter mode: 'all' shows all settings, 'quality' shows quality/speed, 'captions' shows subtitles/position
  mode?: "all" | "quality" | "captions";
  // Quality selection
  levels: ReadonlyArray<Level>;
  selectedLevel: number | "auto";
  onChangeLevel: (val: number | "auto") => void;
  // Playback speed
  playbackRate: number;
  onChangePlaybackRate: (rate: number) => void;
  // Captions
  captions: ReadonlyArray<CaptionTrack>;
  activeCaption: number | "off";
  onChangeCaption: (val: number | "off") => void;
  // Subtitle position
  subtitlePosition: SubtitlePosition;
  onChangeSubtitlePosition: (position: SubtitlePosition) => void;
  // Subtitle font size
  subtitleFontSize: FontSize;
  onChangeSubtitleFontSize: (size: FontSize) => void;
  // Subtitle background opacity
  subtitleBackgroundOpacity: BackgroundOpacity;
  onChangeSubtitleBackgroundOpacity: (opacity: BackgroundOpacity) => void;
  // Subtitle text color
  subtitleTextColor: TextColor;
  onChangeSubtitleTextColor: (color: TextColor) => void;
  // Subtitle font family
  subtitleFontFamily: FontFamily;
  onChangeSubtitleFontFamily: (family: FontFamily) => void;
  // Subtitle text edge style
  subtitleTextEdge: TextEdgeStyle;
  onChangeSubtitleTextEdge: (edge: TextEdgeStyle) => void;
}>;

type SettingsView =
  | "main"
  | "quality"
  | "speed"
  | "captions"
  | "subtitlePosition"
  | "subtitleFontSize"
  | "subtitleBackground"
  | "subtitleTextColor"
  | "subtitleFontFamily"
  | "subtitleTextEdge";

/**
 * Available playback speed options
 * Ranges from 0.25x (quarter speed) to 2x (double speed)
 */
const PLAYBACK_SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

/**
 * Subtitle position options with display labels
 */
const SUBTITLE_POSITIONS: Array<{ value: SubtitlePosition; label: string }> = [
  { value: "auto", label: "Auto" },
  { value: "top", label: "Top" },
  { value: "middle", label: "Middle" },
  { value: "bottom", label: "Bottom" },
];

/**
 * Font size options with display labels
 */
const FONT_SIZES: Array<{ value: FontSize; label: string }> = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "x-large", label: "Extra Large" },
];

/**
 * Background opacity options with display labels
 */
const BACKGROUND_OPACITIES: Array<{ value: BackgroundOpacity; label: string }> = [
  { value: 0, label: "None (0%)" },
  { value: 25, label: "Light (25%)" },
  { value: 50, label: "Medium (50%)" },
  { value: 75, label: "Strong (75%)" },
  { value: 100, label: "Solid (100%)" },
];

/**
 * Text color options with display labels
 */
const TEXT_COLORS: Array<{ value: TextColor; label: string }> = [
  { value: "white", label: "White" },
  { value: "yellow", label: "Yellow" },
  { value: "gray", label: "Gray" },
  { value: "black", label: "Black" },
];

/**
 * Font family options with display labels
 */
const FONT_FAMILIES: Array<{ value: FontFamily; label: string }> = [
  { value: "proportional-sans", label: "Sans-Serif" },
  { value: "monospace-sans", label: "Monospace Sans" },
  { value: "proportional-serif", label: "Serif" },
  { value: "monospace-serif", label: "Monospace Serif" },
];

/**
 * Text edge style options with display labels
 */
const TEXT_EDGE_STYLES: Array<{ value: TextEdgeStyle; label: string }> = [
  { value: "none", label: "None" },
  { value: "drop-shadow", label: "Drop Shadow" },
  { value: "drop-shadow-strong", label: "Strong Shadow" },
  { value: "uniform", label: "Uniform" },
];

type SubmenuProps = Readonly<{
  view: SettingsView;
  levels: ReadonlyArray<Level>;
  selectedLevel: number | "auto";
  onQualityClick: (levelId: number | "auto") => void;
  playbackRate: number;
  onSpeedClick: (speed: number) => void;
  captions: ReadonlyArray<CaptionTrack>;
  activeCaption: number | "off";
  onCaptionClick: (captionId: number | "off") => void;
  subtitlePosition: SubtitlePosition;
  onSubtitlePositionClick: (position: SubtitlePosition) => void;
  subtitleFontSize: FontSize;
  onSubtitleFontSizeClick: (size: FontSize) => void;
  subtitleBackgroundOpacity: BackgroundOpacity;
  onSubtitleBackgroundOpacityClick: (opacity: BackgroundOpacity) => void;
  subtitleTextColor: TextColor;
  onSubtitleTextColorClick: (color: TextColor) => void;
  subtitleFontFamily: FontFamily;
  onSubtitleFontFamilyClick: (family: FontFamily) => void;
  subtitleTextEdge: TextEdgeStyle;
  onSubtitleTextEdgeClick: (edge: TextEdgeStyle) => void;
  onBack: () => void;
}>;

function SettingsSubmenu(props: SubmenuProps): ReactElement | null {
  const {
    view,
    levels,
    selectedLevel,
    onQualityClick,
    playbackRate,
    onSpeedClick,
    captions,
    activeCaption,
    onCaptionClick,
    subtitlePosition,
    onSubtitlePositionClick,
    subtitleFontSize,
    onSubtitleFontSizeClick,
    subtitleBackgroundOpacity,
    onSubtitleBackgroundOpacityClick,
    subtitleTextColor,
    onSubtitleTextColorClick,
    subtitleFontFamily,
    onSubtitleFontFamilyClick,
    subtitleTextEdge,
    onSubtitleTextEdgeClick,
    onBack,
  } = props;

  if (view === "quality") {
    return (
      <div className="py-2">
        <SettingsBackButton onClick={onBack} />
        <SettingsOptionItem label="Auto" isSelected={selectedLevel === "auto"} onClick={() => onQualityClick("auto")} />
        {levels
          .filter((level) => !level.isAuto)
          .sort((a, b) => (b.height ?? 0) - (a.height ?? 0))
          .map((level) => (
            <SettingsOptionItem
              key={level.id}
              label={level.label}
              isSelected={selectedLevel === level.id}
              onClick={() => onQualityClick(level.id)}
            />
          ))}
      </div>
    );
  }

  if (view === "speed") {
    return (
      <div className="py-2">
        <SettingsBackButton onClick={onBack} />
        {PLAYBACK_SPEEDS.map((speed) => (
          <SettingsOptionItem
            key={speed}
            label={speed === 1 ? "Normal" : `${speed}x`}
            isSelected={Math.abs(playbackRate - speed) < 0.01}
            onClick={() => onSpeedClick(speed)}
          />
        ))}
      </div>
    );
  }

  if (view === "captions") {
    return (
      <div className="py-2">
        <SettingsBackButton onClick={onBack} />
        <SettingsOptionItem label="Off" isSelected={activeCaption === "off"} onClick={() => onCaptionClick("off")} />
        {captions.map((caption) => (
          <SettingsOptionItem
            key={caption.id}
            label={caption.label || caption.lang}
            isSelected={activeCaption === caption.id}
            onClick={() => onCaptionClick(caption.id)}
          />
        ))}
      </div>
    );
  }

  if (view === "subtitlePosition") {
    return (
      <div className="py-2">
        <SettingsBackButton onClick={onBack} />
        {SUBTITLE_POSITIONS.map((pos) => (
          <SettingsOptionItem
            key={pos.value}
            label={pos.label}
            isSelected={subtitlePosition === pos.value}
            onClick={() => onSubtitlePositionClick(pos.value)}
          />
        ))}
      </div>
    );
  }

  if (view === "subtitleFontSize") {
    return (
      <div className="py-2">
        <SettingsBackButton onClick={onBack} />
        {FONT_SIZES.map((size) => (
          <SettingsOptionItem
            key={size.value}
            label={size.label}
            isSelected={subtitleFontSize === size.value}
            onClick={() => onSubtitleFontSizeClick(size.value)}
          />
        ))}
      </div>
    );
  }

  if (view === "subtitleBackground") {
    return (
      <div className="py-2">
        <SettingsBackButton onClick={onBack} />
        {BACKGROUND_OPACITIES.map((opacity) => (
          <SettingsOptionItem
            key={opacity.value}
            label={opacity.label}
            isSelected={subtitleBackgroundOpacity === opacity.value}
            onClick={() => onSubtitleBackgroundOpacityClick(opacity.value)}
          />
        ))}
      </div>
    );
  }

  if (view === "subtitleTextColor") {
    return (
      <div className="py-2">
        <SettingsBackButton onClick={onBack} />
        {TEXT_COLORS.map((color) => (
          <SettingsOptionItem
            key={color.value}
            label={color.label}
            isSelected={subtitleTextColor === color.value}
            onClick={() => onSubtitleTextColorClick(color.value)}
          />
        ))}
      </div>
    );
  }

  if (view === "subtitleFontFamily") {
    return (
      <div className="py-2">
        <SettingsBackButton onClick={onBack} />
        {FONT_FAMILIES.map((family) => (
          <SettingsOptionItem
            key={family.value}
            label={family.label}
            isSelected={subtitleFontFamily === family.value}
            onClick={() => onSubtitleFontFamilyClick(family.value)}
          />
        ))}
      </div>
    );
  }

  if (view === "subtitleTextEdge") {
    return (
      <div className="py-2">
        <SettingsBackButton onClick={onBack} />
        {TEXT_EDGE_STYLES.map((edge) => (
          <SettingsOptionItem
            key={edge.value}
            label={edge.label}
            isSelected={subtitleTextEdge === edge.value}
            onClick={() => onSubtitleTextEdgeClick(edge.value)}
          />
        ))}
      </div>
    );
  }

  return null;
}

export function SettingsPanel(props: SettingsPanelProps): ReactElement | null {
  const {
    isOpen,
    onClose,
    mode = "all",
    levels,
    selectedLevel,
    onChangeLevel,
    playbackRate,
    onChangePlaybackRate,
    captions,
    activeCaption,
    onChangeCaption,
    subtitlePosition,
    onChangeSubtitlePosition,
    subtitleFontSize,
    onChangeSubtitleFontSize,
    subtitleBackgroundOpacity,
    onChangeSubtitleBackgroundOpacity,
    subtitleTextColor,
    onChangeSubtitleTextColor,
    subtitleFontFamily,
    onChangeSubtitleFontFamily,
    subtitleTextEdge,
    onChangeSubtitleTextEdge,
  } = props;

  const [view, setView] = useState<SettingsView>("main");

  // Reset to main view when panel closes
  useEffect(() => {
    if (!isOpen) {
      setView("main");
    }
  }, [isOpen]);

  // Early return if panel is not open
  if (!isOpen) {
    return null;
  }

  const handleQualityClick = (levelId: number | "auto") => {
    onChangeLevel(levelId);
    setView("main");
  };

  const handleSpeedClick = (speed: number) => {
    onChangePlaybackRate(speed);
    setView("main");
  };

  const handleCaptionClick = (captionId: number | "off") => {
    onChangeCaption(captionId);
    setView("main");
  };

  const handleSubtitlePositionClick = (position: SubtitlePosition) => {
    onChangeSubtitlePosition(position);
    setView("main");
  };

  const handleSubtitleFontSizeClick = (size: FontSize) => {
    onChangeSubtitleFontSize(size);
    setView("main");
  };

  const handleSubtitleBackgroundOpacityClick = (opacity: BackgroundOpacity) => {
    onChangeSubtitleBackgroundOpacity(opacity);
    setView("main");
  };

  const handleSubtitleTextColorClick = (color: TextColor) => {
    onChangeSubtitleTextColor(color);
    setView("main");
  };

  const handleSubtitleFontFamilyClick = (family: FontFamily) => {
    onChangeSubtitleFontFamily(family);
    setView("main");
  };

  const handleSubtitleTextEdgeClick = (edge: TextEdgeStyle) => {
    onChangeSubtitleTextEdge(edge);
    setView("main");
  };

  const handleBack = () => {
    setView("main");
  };

  const handleClose = () => {
    setView("main");
    onClose();
  };

  // Determine header title based on current view and mode
  const getHeaderTitle = (): string => {
    if (view === "quality") return "Quality";
    if (view === "speed") return "Playback Speed";
    if (view === "captions") return "Subtitles/CC";
    if (view === "subtitlePosition") return "Subtitle Position";
    if (view === "subtitleFontSize") return "Subtitle Size";
    if (view === "subtitleBackground") return "Subtitle Background";
    if (view === "subtitleTextColor") return "Text Color";
    if (view === "subtitleFontFamily") return "Font";
    if (view === "subtitleTextEdge") return "Text Edge";

    // Main view titles based on mode
    if (mode === "quality") return "Quality Settings";
    if (mode === "captions") return "Caption Settings";
    return "Settings";
  };

  return (
    <div className="pointer-events-auto absolute bottom-[6rem] left-8 w-80 backdrop-blur-md bg-black/40 border border-neutral-800 rounded-lg shadow-2xl overflow-hidden z-50 transition-opacity duration-300 ease-in-out">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-1 border-b border-neutral-800">
        <h3 className="text-xs font-semibold text-neutral-300 uppercase">{getHeaderTitle()}</h3>
        <Button onClick={handleClose} variant="ghost-sm" aria-label="Close settings" className="">
          <X size={18} />
        </Button>
      </div>

      {/* Main Menu */}
      {view === "main" && (
        <div className="py-2">
          {/* Quality settings - show if mode is 'all' or 'quality' */}
          {(mode === "all" || mode === "quality") && (
            <>
              <SettingsMenuItem
                label="Quality"
                value={
                  selectedLevel === "auto" ? "Auto" : (levels.find((l) => l.id === selectedLevel)?.label ?? "Auto")
                }
                onClick={() => setView("quality")}
                icon={<MonitorPlay size={18} />}
              />
              <SettingsMenuItem
                label="Speed"
                value={playbackRate === 1 ? "Normal" : `${playbackRate}x`}
                onClick={() => setView("speed")}
                icon={<Gauge size={18} />}
              />
            </>
          )}
          {/* Caption settings - show if mode is 'all' or 'captions' */}
          {(mode === "all" || mode === "captions") && (
            <>
              <SettingsMenuItem
                label="Subtitles"
                value={
                  activeCaption === "off" ? "Off" : (captions.find((c) => c.id === activeCaption)?.label ?? "Unknown")
                }
                onClick={() => setView("captions")}
                icon={<Subtitles size={18} />}
              />
              <SettingsMenuItem
                label="Subtitle Position"
                value={subtitlePosition.charAt(0).toUpperCase() + subtitlePosition.slice(1)}
                onClick={() => setView("subtitlePosition")}
                icon={<MoveVertical size={18} />}
              />
              <SettingsMenuItem
                label="Subtitle Size"
                value={subtitleFontSize.charAt(0).toUpperCase() + subtitleFontSize.slice(1).replace("-", " ")}
                onClick={() => setView("subtitleFontSize")}
                icon={<Type size={18} />}
              />
              <SettingsMenuItem
                label="Subtitle Background"
                value={`${subtitleBackgroundOpacity}%`}
                onClick={() => setView("subtitleBackground")}
                icon={<Scan size={18} />}
              />
              <SettingsMenuItem
                label="Text Color"
                value={subtitleTextColor.charAt(0).toUpperCase() + subtitleTextColor.slice(1)}
                onClick={() => setView("subtitleTextColor")}
                icon={<Palette size={18} />}
              />
              <SettingsMenuItem
                label="Font"
                value={FONT_FAMILIES.find((f) => f.value === subtitleFontFamily)?.label || "Sans-Serif"}
                onClick={() => setView("subtitleFontFamily")}
                icon={<Type size={18} />}
              />
              <SettingsMenuItem
                label="Text Edge"
                value={TEXT_EDGE_STYLES.find((e) => e.value === subtitleTextEdge)?.label || "Drop Shadow"}
                onClick={() => setView("subtitleTextEdge")}
                icon={<Wand2 size={18} />}
              />
            </>
          )}
        </div>
      )}

      {/* Submenus */}
      {view !== "main" && (
        <SettingsSubmenu
          view={view}
          levels={levels}
          selectedLevel={selectedLevel}
          onQualityClick={handleQualityClick}
          playbackRate={playbackRate}
          onSpeedClick={handleSpeedClick}
          captions={captions}
          activeCaption={activeCaption}
          onCaptionClick={handleCaptionClick}
          subtitlePosition={subtitlePosition}
          onSubtitlePositionClick={handleSubtitlePositionClick}
          subtitleFontSize={subtitleFontSize}
          onSubtitleFontSizeClick={handleSubtitleFontSizeClick}
          subtitleBackgroundOpacity={subtitleBackgroundOpacity}
          onSubtitleBackgroundOpacityClick={handleSubtitleBackgroundOpacityClick}
          subtitleTextColor={subtitleTextColor}
          onSubtitleTextColorClick={handleSubtitleTextColorClick}
          subtitleFontFamily={subtitleFontFamily}
          onSubtitleFontFamilyClick={handleSubtitleFontFamilyClick}
          subtitleTextEdge={subtitleTextEdge}
          onSubtitleTextEdgeClick={handleSubtitleTextEdgeClick}
          onBack={handleBack}
        />
      )}
    </div>
  );
}

type SettingsMenuItemProps = Readonly<{
  label: string;
  value: string;
  onClick: () => void;
  icon?: React.ReactNode;
}>;

function SettingsMenuItem({ label, value, onClick, icon }: SettingsMenuItemProps) {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-2.5 flex items-center justify-between text-sm text-white hover:bg-neutral-800/50 transition-colors group"
    >
      <div className="flex items-center gap-3">
        {icon && <span className="text-neutral-400 group-hover:text-neutral-200 transition-colors">{icon}</span>}
        <span className="text-neutral-300 group-hover:text-white transition-colors">{label}</span>
      </div>
      <span className="text-white font-medium">{value}</span>
    </button>
  );
}

type SettingsOptionItemProps = Readonly<{
  label: string;
  isSelected: boolean;
  onClick: () => void;
}>;

function SettingsOptionItem({ label, isSelected, onClick }: SettingsOptionItemProps) {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-2.5 flex items-center justify-between text-sm text-white hover:bg-neutral-800/50 transition-colors"
    >
      <span className={isSelected ? "text-white font-medium" : "text-neutral-300"}>{label}</span>
      {isSelected && <Check size={16} className="text-primary-400" />}
    </button>
  );
}

type SettingsBackButtonProps = Readonly<{
  onClick: () => void;
}>;

function SettingsBackButton({ onClick }: SettingsBackButtonProps) {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-2.5 text-left text-sm text-neutral-400 hover:bg-neutral-800/50 transition-colors border-b border-neutral-800 mb-1"
    >
      ← Back
    </button>
  );
}
